import React from 'react';
import { useThemedStyles } from '../../theme/useThemedStyles';

const DISMISS_KEY = 'dentec:pwaInstallDismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export default function InstallPrompt() {
  const theme = useThemedStyles();
  const [deferredPrompt, setDeferredPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = React.useState(false);
  const [isDismissed, setIsDismissed] = React.useState(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [isPrompting, setIsPrompting] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    setIsInstalled(mediaQuery.matches || (window.navigator as Navigator & { standalone?: boolean }).standalone === true);

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const onInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      localStorage.removeItem(DISMISS_KEY);
      setIsDismissed(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setIsDismissed(true);
  };

  const onInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    setIsPrompting(true);
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } finally {
      setIsPrompting(false);
    }
  };

  if (isInstalled || isDismissed || !deferredPrompt) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: theme.surface,
        color: theme.text,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        padding: '10px 14px',
        boxShadow: theme.shadow,
        maxWidth: 'min(360px, calc(100vw - 32px))'
      }}
    >
      <div style={{ fontSize: 13, lineHeight: 1.3 }}>
        Install Clinix for quicker access and better offline support.
      </div>
      <button
        type="button"
        onClick={onInstall}
        disabled={isPrompting}
        style={{
          border: 'none',
          borderRadius: 8,
          background: theme.primary,
          color: theme.primaryText,
          padding: '8px 10px',
          fontWeight: 700,
          cursor: isPrompting ? 'wait' : 'pointer'
        }}
      >
        {isPrompting ? 'Installing...' : 'Install'}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        style={{
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          background: 'transparent',
          color: theme.text,
          padding: '8px 10px',
          cursor: 'pointer'
        }}
      >
        Later
      </button>
    </div>
  );
}
